import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Modal } from 'antd'
import { useAppSelector, useAppDispatch } from '../../app/hooks'
import ReactCrop from 'react-image-crop'
import type { Crop } from 'react-image-crop'
import { v4 as uuid } from 'uuid'
import 'react-image-crop/dist/ReactCrop.css'
import actionTypes from '../../constants/actionTypes'
import { fetchUploadToken, uploadToOSS } from '../../actions/main'
import { capturePromiseException } from '../../utils/sentryUtils'

const bucketName = 'net-cloud'

interface ImageCropProps {
  src: string
  onOk: (url: string) => void
  [key: string]: any
}

function ImageCrop(props: ImageCropProps) {
  const { src, onOk, ...restProps } = props
  const dispatch = useAppDispatch()
  const ossToken = useAppSelector((state) => state.main.ossToken)

  const [crop, setCrop] = useState<Crop>({
    unit: '%',
    width: 50,
    height: 50,
    x: 25,
    y: 25
  })
  const [croppedImageUrl, setCroppedImageUrl] = useState<Blob | null>(null)
  const [cropWidth, setCropWidth] = useState('')

  const imageRef = useRef<HTMLImageElement | null>(null)

  useEffect(() => {
    const ossDataStr = localStorage.getItem('ossData')
    const ossData = ossDataStr ? JSON.parse(ossDataStr) : null
    if (
      !ossData ||
      !ossData[bucketName] ||
      ossData[bucketName].expire < Date.now() / 1000
    ) {
      dispatch(fetchUploadToken())
    } else {
      dispatch({
        type: actionTypes.SET_OSS_TOKEN,
        data: ossData
      })
    }
  }, [dispatch])

  const getCroppedImg = useCallback((image: HTMLImageElement, cropData: Crop) => {
    const canvas = document.createElement('canvas')
    const scaleX = image.naturalWidth / image.width
    const scaleY = image.naturalHeight / image.height
    canvas.width = cropData.width || 0
    canvas.height = cropData.height || 0
    const ctx = canvas.getContext('2d')

    ctx?.drawImage(
      image,
      cropData.x * scaleX,
      cropData.y * scaleY,
      cropData.width * scaleX,
      cropData.height * scaleY,
      0,
      0,
      cropData.width,
      cropData.height
    )

    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (!blob) {
          console.error('Canvas is empty')
          return
        }
        resolve(blob)
      }, 'image/png')
    })
  }, [])

  const makeClientCrop = useCallback(
    async (cropData: Crop) => {
      let croppedBlob: Blob | null = null
      if (imageRef.current && cropData.width && cropData.height) {
        croppedBlob = await getCroppedImg(imageRef.current, cropData) as Blob
      } else if (imageRef.current) {
        croppedBlob = await getCroppedImg(imageRef.current, {
          unit: 'px',
          x: 0,
          y: 0,
          width: imageRef.current.width,
          height: imageRef.current.height
        }) as Blob
      }
      setCroppedImageUrl(croppedBlob)
    },
    [getCroppedImg]
  )

  const onCropComplete = (cropData: Crop) => {
    makeClientCrop(cropData)
  }

  const onImageLoaded = (image: HTMLImageElement) => {
    imageRef.current = image
    image.naturalWidth <= 520 && setCropWidth(image.naturalWidth + 'px')
  }

  const onCropChange = (newCrop: Crop) => {
    setCrop(newCrop)
  }

  const handleUpload = async () => {
    if (!croppedImageUrl || !ossToken) return
    const filename = uuid() + '.png'
    uploadToOSS({
      key: `group/logo/${filename}`,
      OSSAccessKeyId: ossToken[bucketName]?.accessid,
      policy: ossToken[bucketName]?.policy,
      Signature: ossToken[bucketName]?.signature,
      file: new File([croppedImageUrl], filename, {
        type: 'image/png',
        lastModified: Date.now()
      })
    })
      .then(() => {
        onOk(`http://net-cloud.xhey.top/group/logo/${filename}`)
      })
      .catch(capturePromiseException)
  }

  const cropProps = {
    minWidth: 20,
    minHeight: 20
  }

  return (
    <Modal
      open={!!src}
      title="裁剪图片"
      width={568}
      {...restProps}
      onOk={handleUpload}
    >
      <div style={{ width: cropWidth, maxWidth: '520px', margin: '0 auto' }}>
        <ReactCrop
          src={src}
          crop={crop}
          {...cropProps}
          ruleOfThirds
          onImageLoaded={onImageLoaded}
          onComplete={onCropComplete}
          onChange={onCropChange}
        />
      </div>
    </Modal>
  )
}

export default ImageCrop
